import { Duration } from '@js-joda/core';
import type {
    AccessSourceType,
    PermissionDirective,
    PermissionType,
} from '@thinkalpha/common/contracts/permissions.js';
import type { DetailedUser, NewUser, PaginatedUserSearchResponse, User } from '@thinkalpha/ui-types/types/admin/users';
import { sortBy } from 'es-toolkit';
import { createInstance, createLegacyInstance } from 'src/api';
import { getExpirationDate } from 'src/features/oauth2';
import { appConfig } from 'src/lib/config';
import type { PatchUser, UpdateUser } from 'src/lib/user/types';
import type { TokenState } from 'src/store/reducers/auth';

const accessApi = createInstance({ baseURL: appConfig.accessApi });
const legacyAccessApi = createLegacyInstance({ baseURL: appConfig.accessApi });

export interface Access {
    type: AccessSourceType;
    id: string;
    permission: PermissionType;
}
export interface ODataQueryParameters {
    $filter?: string;
    $search?: string;
    $orderby?: string;
    $count?: boolean;
    $top?: number;
    $skip?: number;
    $select?: string | undefined;
}

export type ODataSearchParamsType = {
    $filter?: string;
    $search?: string;
    $orderby?: string;
    $count?: 'true' | 'false';
    $top?: string;
    $skip?: string;
    $select?: string;
};

export async function getUser(userId: string): Promise<DetailedUser> {
    const { data } = await accessApi.get<DetailedUser>(`/users/${userId}`);
    return data;
}

export const updateUser = ({ userId, updates }: UpdateUser): Promise<User> => {
    return accessApi.patch<User>(`/users/${userId}`, updates).then((x) => x.data);
};

export async function getUsers(userIds: string[] | PermissionDirective[]): Promise<DetailedUser[]> {
    const { data } = await legacyAccessApi.get<DetailedUser[]>('users/bulk', {
        params: {
            ids: userIds,
        },
    });
    return data;
}

export const createUser = (model: NewUser) => {
    return legacyAccessApi.post<{ user: User }>(`/users`, model);
};

export const activateUser = (id: string, isActive: boolean) => {
    return legacyAccessApi.put<void>(`/users/${id}/active`, { active: isActive });
};
/**
 * @deprecated - use updateUser from http and getUpdateUserMutation from queries.
 */
export const editUser = (id: string, userInfo: PatchUser) => {
    return legacyAccessApi.patch<User>(`/users/${id}`, userInfo);
};

export const changePassword = (id: string, password: string) => {
    return legacyAccessApi.put<User>(`/users/${id}/password`, {
        password,
    });
};

// Keycloak only treats the input as a wildcard search when prefixed with `*`;
// without it, inputs containing characters like digits or `+` may return no matches.
const toWildcardSearch = (search: string): string => (search && !search.startsWith('*') ? `*${search}` : search);

export async function searchUsers(search: string): Promise<User[]> {
    const { data } = await legacyAccessApi.get<User[]>(`/users/search`, {
        params: { $search: toWildcardSearch(search) },
    });
    const sortedUsers = sortBy(data, [(user) => `${user.firstName} ${user.lastName}`.toLowerCase()]);
    return sortedUsers;
}

export async function paginatedSearchForUsers(
    search: string,
    queryParams: ODataQueryParameters = {},
): Promise<PaginatedUserSearchResponse> {
    const params = {
        $search: toWildcardSearch(search),
        $orderby: queryParams.$orderby ?? undefined,
        $count: queryParams.$count ?? true,
        $top: queryParams.$top ?? undefined,
        $skip: queryParams.$skip ?? 0,
    };
    const { data } = await legacyAccessApi.get<PaginatedUserSearchResponse>(`/users/search`, { params: { ...params } });
    const sortedUsers = sortBy(data.results, [(user) => `${user.firstName} ${user.lastName}`.toLowerCase()]);
    return {
        ...data,
        results: sortedUsers,
    };
}

export const resetPassword = (id: string) => {
    return legacyAccessApi.post<User>(`/users/${id}/password-reset`);
};

export async function getUserAccessList(id: string) {
    const { data } = await legacyAccessApi.get<Access[]>(`/users/${id}/access-list`);
    return data;
}

export async function getUserRelatedFirmUsers(userId: string, firmId?: string) {
    const params = firmId ? { firmId } : {};
    const { data } = await legacyAccessApi.get<User[]>(`/users/${userId}/related-firm-users`, { params });
    return data;
}

/**
 * Get a user's JWT token from access-ws as a TokenState object
 */
export async function getUserAccessToken(userId: string, duration: Duration): Promise<TokenState> {
    const params = { sessionTimeInSeconds: Duration.ofSeconds(duration.seconds()).seconds() };
    const { data } = await legacyAccessApi.get<{ source: { token: string } }>(`/users/${userId}/token`, { params });

    return {
        token: data.source.token,
        expires: getExpirationDate(data.source.token),
    };
}
